import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAuth } from '../../contexts/AuthContext';
import { emailMonitorAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface HistoryItem {
  id: number;
  domain: string;
  record_type: string;
  selector: string | null;
  change_type: 'added' | 'modified' | 'removed';
  old_value: string | null;
  new_value: string | null;
  detected_at: string;
}

const RECORD_TYPES = [
  { value: '', label: 'All Records' },
  { value: 'spf', label: 'SPF' },
  { value: 'dkim', label: 'DKIM' },
  { value: 'dmarc', label: 'DMARC' },
  { value: 'mx', label: 'MX' },
];

const DAY_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const EmailDnsHistoryPage: React.FC = () => {
  useDocumentTitle('DNS History');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [recordTypeFilter, setRecordTypeFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(30);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!currentAsset) return;
    try {
      setLoading(true);
      const params: any = { days: daysFilter };
      if (recordTypeFilter) params.record_type = recordTypeFilter;
      const response = await emailMonitorAPI.getDnsHistory(currentAsset.id, params);
      if (response.data.status === 'success') {
        setHistory(response.data.data.history || []);
      }
    } catch (error) {
      console.error('Failed to fetch DNS history:', error);
      toast.error('Failed to load DNS change history');
    } finally {
      setLoading(false);
    }
  }, [currentAsset, recordTypeFilter, daysFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getChangeBadge = (type: string) => {
    switch (type) {
      case 'added': return <span className="badge bg-success">Added</span>;
      case 'removed': return <span className="badge bg-danger">Removed</span>;
      case 'modified': return <span className="badge bg-warning text-dark">Modified</span>;
      default: return <span className="badge bg-secondary">{type}</span>;
    }
  };

  if (!currentAsset) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <i className="bi bi-clock-history" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
            <p className="mt-3 text-muted">Please select an asset to view DNS history</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid p-0">
        <div className="mb-4">
          <h4 className="mb-1 fw-bold">DNS Change History</h4>
          <p className="text-muted mb-0">Track changes to your domain's email DNS records over time</p>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold"><small>Record Type</small></label>
                <select
                  className="form-select form-select-sm"
                  value={recordTypeFilter}
                  onChange={(e) => setRecordTypeFilter(e.target.value)}
                >
                  {RECORD_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold"><small>Time Period</small></label>
                <select
                  className="form-select form-select-sm"
                  value={daysFilter}
                  onChange={(e) => setDaysFilter(parseInt(e.target.value))}
                >
                  {DAY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* History Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                <p className="mt-2 text-muted mb-0">Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-check-circle" style={{ fontSize: '3rem', color: '#059669' }}></i>
                <h6 className="mt-3">No Changes Detected</h6>
                <p className="text-muted mb-0">No DNS record changes have been detected in the selected time period.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Domain</th>
                      <th>Record</th>
                      <th>Change</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <React.Fragment key={item.id}>
                        <tr
                          onClick={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td><small>{formatDate(item.detected_at)}</small></td>
                          <td>{item.domain}</td>
                          <td>
                            <span className="badge bg-secondary">{item.record_type.toUpperCase()}</span>
                            {item.selector && <small className="ms-1 text-muted">({item.selector})</small>}
                          </td>
                          <td>{getChangeBadge(item.change_type)}</td>
                          <td>
                            <i className={`bi bi-chevron-${expandedRow === item.id ? 'up' : 'down'} text-muted`}></i>
                          </td>
                        </tr>
                        {expandedRow === item.id && (
                          <tr>
                            <td colSpan={5} className="bg-light">
                              <div className="p-3">
                                {item.old_value && (
                                  <div className="mb-2">
                                    <small className="fw-semibold text-danger">Old Value:</small>
                                    <div className="bg-white rounded p-2 mt-1" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                      {item.old_value}
                                    </div>
                                  </div>
                                )}
                                {item.new_value && (
                                  <div>
                                    <small className="fw-semibold text-success">New Value:</small>
                                    <div className="bg-white rounded p-2 mt-1" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                      {item.new_value}
                                    </div>
                                  </div>
                                )}
                                {!item.old_value && !item.new_value && (
                                  <small className="text-muted">No value details available</small>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmailDnsHistoryPage;
